import { useInfiniteQuery, useQuery } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { retryOnTransient } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import type { AgentSummaryResponse, ToolCallFilters, ToolCallPage } from 'src/types/AgentActivity';
import { getToolCalls, getToolCallSummary } from '../functions/agentActivityApi';

/** Newest first; `fetchNextPage` follows the server's cursor. */
export function useToolCallsQuery(filters: ToolCallFilters) {
  const accessToken = useAccessToken();
  return useInfiniteQuery<ToolCallPage, FetchError>(
    [queryNames.agentToolCalls, filters],
    ({ pageParam }) => queryFnFetchErrorWrapper(() => getToolCalls(accessToken, filters, pageParam))(),
    {
      getNextPageParam: (last) => last.data.next_cursor ?? undefined,
      // A 403 (not an admin) or 404 (not in your account) is an answer.
      retry: retryOnTransient,
      enabled: !!accessToken,
      keepPreviousData: true,
    },
  );
}

export function useToolCallSummaryQuery(filters: ToolCallFilters) {
  const accessToken = useAccessToken();
  return useQuery<AgentSummaryResponse, FetchError>(
    [queryNames.agentToolCallSummary, filters],
    queryFnFetchErrorWrapper(() => getToolCallSummary(accessToken, filters)),
    { retry: retryOnTransient, enabled: !!accessToken, keepPreviousData: true },
  );
}
